import * as React from 'react'
import { useState, useMemo } from "react";
import { Variant, VariantCardProps, VariantGroup } from "./ProductVariants.js";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel.js"
import { capitalizeWords, getGridColsClass } from './DesktopVariants.js';
import { useOV25UI } from '../../contexts/ov25-ui-context.js';
import { VariantsContent } from './VariantsContent.js';
import { FilterControls } from './FilterControls.js';
import { FilterContent } from './FilterContent.js';
import { NoResults } from './NoResults.js';
import { cn } from '../../lib/utils.js';


const VariantsContentWithCarousel = React.memo(({ variantsToRender, VariantCard, onSelect, isMobile, isGrouped = false }: { variantsToRender: Variant[], VariantCard: React.ComponentType<VariantCardProps>, onSelect: (variant: Variant) => void, isMobile: boolean, isGrouped: boolean }) => {
    const {allOptions, activeOptionId} = useOV25UI()
    const activeOption = allOptions.find((option) => option.id === activeOptionId)
    const activeOptionName = activeOption?.name ?? ''

    return (
      <>
        <Carousel opts={{ dragFree: true, loop: false }}>
          <CarouselContent>
            <CarouselItem key={`spacer`} className="ov:cursor-pointer ov:basis-[9%]">
            </CarouselItem>
            {variantsToRender.map((variant, index) => {
              const basis =  activeOptionName.toLowerCase() === 'size' ? 2  : (activeOptionName.toLowerCase() === 'modules' ? 2 : 5)
              const basisClass = {
                1: 'ov:basis-full',
                2: 'ov:basis-[45%]',
                3: 'ov:basis-1/3',
                4: 'ov:basis-1/4',
                5: 'ov:basis-[20%]'
              }[basis];
              return (
                <CarouselItem key={variant.id + (variant.groupId || '') + (variant.optionId || '')} className={cn('ov:cursor-pointer', basisClass)}>
                  <VariantCard
                    variant={variant}
                    onSelect={onSelect}
                    index={index}
                    isMobile={isMobile}
                    isGrouped={isGrouped}
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </>
    )
  }, (prevProps, nextProps) => {
    // Only re-render if the variants array reference changes or length changes
    if (prevProps.variantsToRender.length !== nextProps.variantsToRender.length) {
      return false;
    }
    
    // Check if variants are the same by comparing IDs, images, and names
    // Also check isSelected - we need to re-render when selection state changes
    for (let i = 0; i < prevProps.variantsToRender.length; i++) {
      const prev = prevProps.variantsToRender[i];
      const next = nextProps.variantsToRender[i];
      if (
        prev.id !== next.id || 
        prev.image !== next.image || 
        prev.name !== next.name ||
        prev.isSelected !== next.isSelected
      ) {
        return false; // Re-render needed
      }
    }
    
    // Only skip re-render if everything is the same
    return (
      prevProps.isMobile === nextProps.isMobile &&
      prevProps.isGrouped === nextProps.isGrouped
    );
  });

VariantsContentWithCarousel.displayName = 'VariantsContentWithCarousel';

const MobileVariantsContent = React.memo(({ variants, VariantCard, isMobile, onSelect, gridDivide, isFilterOpen, setIsFilterOpen, moduleTypeTabs }: {
  variants: VariantGroup[] | Variant[],
  VariantCard: React.ComponentType<VariantCardProps>,
  isMobile: boolean,
  onSelect: (variant: Variant) => void,
  gridDivide: number,
  isFilterOpen: boolean,
  setIsFilterOpen: (isOpen: boolean) => void,
  moduleTypeTabs?: React.ReactNode
}) => {
  const {
      drawerSize,
      availableProductFilters,
      showFilters,
      activeOption,
      allOptions,
      activeOptionId,
  } = useOV25UI();

  // When filters change, we need to select the first group selected in the filters. 
  React.useEffect(() => {
    if (isGrouped) {
      const firstGroupSelectedInFilters = availableProductFilters?.[activeOption?.name || '']?.['Collections']?.filter((a) => a.checked)?.some((a) => a.value === (variants[0] as VariantGroup).groupName)
      if (firstGroupSelectedInFilters) {
        setSelectedGroupIndex(availableProductFilters?.[activeOption?.name || '']?.['Collections']?.filter((a) => a.checked)?.findIndex((a) => a.value === (variants[0] as VariantGroup).groupName) || 0);
      }
    }
  }, [availableProductFilters]);

  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);

  const isGrouped = Array.isArray(variants) && variants.length > 0 && 'groupName' in variants[0]
  // If the singular group is selected in the filters, dont destructure
  const singleGroupIsSelectedInFilters = activeOption?.name ? (
    availableProductFilters?.[activeOption.name]?.['Collections']?.filter((a) => a.checked)?.some((a) => a.value === (variants[0] as VariantGroup).groupName)
  ) : false;
  const shouldDestructureGroups = isGrouped && variants.length < 2 && !singleGroupIsSelectedInFilters

  const variantsToRender = useMemo(() => 
      isGrouped ? (variants as VariantGroup[])[0].variants : variants as Variant[],
      [isGrouped, variants]
  );
  const shouldShowFilters = ((isGrouped && !shouldDestructureGroups) && variants.length > 0 && (variants as VariantGroup[]).some(group => group.variants.length > 0)) || showFilters;

  const currentOption = allOptions.find(opt => opt.id === activeOptionId);

  if (isGrouped && !shouldDestructureGroups) {
    // Render all groups but hide inactive ones with display: none
    // This keeps components mounted, preventing image reloads when switching groups
    return (
      <div id="ov25-mobile-filter-container" className="ov:relative ov:w-full ov:h-full ov:flex ov:flex-col">
        {drawerSize !== 'small' && currentOption?.name.toLowerCase() !== 'modules' && (
          <FilterControls 
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            isGrouped={isGrouped && !shouldDestructureGroups}
          />
        )}
        {moduleTypeTabs && currentOption?.name.toLowerCase() === 'modules' && (
          <div className="ov:w-full ov:shrink-0">
            {moduleTypeTabs}
          </div>
        )}
        <div id="ov25-mobile-content-area" className="ov:relative ov:flex-1 ov:overflow-hidden ov:min-h-0">
          <Carousel opts={{ dragFree: true, loop: false }} className="ov:py-2">
            <CarouselContent className="ov:px-4 ov:-ml-2 ov:pr-4">
              <CarouselItem key={'placeholder'} className="ov:basis-[37%] ov:py-2">
                <div className="ov:w-full ov:h-full ov:bg-transparent"></div>
              </CarouselItem>
              {(variants as VariantGroup[]).map((group, index) => {
                return group.variants.length > 0 ? (
                  <CarouselItem key={group.groupName} className="ov:py-2 ov:max-w-full ov:basis-auto">
                    <button
                      onClick={() => setSelectedGroupIndex(index)}
                      className={`ov25-group-control ov:w-auto ov:py-1 ov:px-4 ov:rounded-full ov:shadow-md ov:text-neutral-500 ov:border ${
                        selectedGroupIndex === index
                          ? 'ov:border-neutral-900 ov:text-neutral-900'
                          : ''
                      } ov:flex ov:items-center ov:justify-center ov:text-xs ov:gap-2 ov:whitespace-nowrap`}
                    >
                      <span className="ov:truncate ov:py-1">{capitalizeWords(group.groupName)}</span>
                    </button>
                  </CarouselItem>
                ) : null;
              })}
            </CarouselContent>
          </Carousel>

          {/* Render all groups, hiding inactive ones */}
          <div className="ov:relative ov:h-full">
            {(variants as VariantGroup[]).map((group, index) => {
              const isActive = selectedGroupIndex === index;
              if (group.variants.length === 0) return null;
              
              return (
                <div
                  key={group.groupName}
                  className="ov:absolute ov:inset-0"
                  style={{ display: isActive ? 'block' : 'none' }}
                >
                  {drawerSize === 'small' ? (
                    <VariantsContentWithCarousel 
                      variantsToRender={group.variants}
                      VariantCard={VariantCard}
                      onSelect={onSelect}
                      isMobile={isMobile}
                      isGrouped={isGrouped && !shouldDestructureGroups}
                    />
                  ) : (
                    <>
                      <div id="ov25-mobile-variants-content" className={`ov:h-full ${isFilterOpen ? 'ov:overflow-hidden' : 'ov:overflow-y-auto'}`}>
                        <div style={{ display: 'grid' }} className={`ov:px-2 ov:pb-63 ${getGridColsClass(gridDivide)}`}>
                          <VariantsContent variantsToRender={group.variants} VariantCard={VariantCard} isMobile={isMobile} onSelect={onSelect} />
                        </div>
                      </div>
                      {shouldShowFilters && (
                        <FilterContent wrapperVariant="mobile" isOpen={isFilterOpen} />
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {/* Show NoResults if no groups have variants */}
            {(variants as VariantGroup[]).every(group => group.variants.length === 0) && (
              <NoResults />
            )}
          </div>
        </div>
      </div>
    );
  } else {
    if (drawerSize === 'small') {
      return (
        <div id="ov25-mobile-filter-container" className="ov:relative ov:w-full ov:h-full ov:flex ov:flex-col">
          {moduleTypeTabs && currentOption?.name.toLowerCase() === 'modules' && (
            <div className="ov:w-full ov:shrink-0">
              {moduleTypeTabs}
            </div>
          )}
          <div id="ov25-mobile-content-area" className="ov:relative ov:flex-1 ov:overflow-hidden ov:min-h-0">
            {variantsToRender.length === 0 && (
            <NoResults />
            )}
            <VariantsContentWithCarousel 
              variantsToRender={variantsToRender}
              VariantCard={VariantCard}
              onSelect={onSelect}
              isMobile={isMobile}
              isGrouped={isGrouped && !shouldDestructureGroups}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div id="ov25-mobile-filter-container" className="ov:relative ov:w-full ov:h-full ov:flex ov:flex-col">
          {currentOption?.name.toLowerCase() !== 'modules' && <FilterControls 
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            isGrouped={isGrouped && !shouldDestructureGroups}
          />}
          {moduleTypeTabs && currentOption?.name.toLowerCase() === 'modules' && (
            <div className="ov:w-full ov:shrink-0">
              {moduleTypeTabs}
            </div>
          )}
          <div id="ov25-mobile-content-area" className="ov:relative ov:flex-1 ov:overflow-hidden ov:min-h-0">
            {variantsToRender.length === 0 && (
            <NoResults />
            )}
            <div id="ov25-mobile-variants-content" className={`ov:h-full ${isFilterOpen ? 'ov:overflow-hidden' : 'ov:overflow-y-auto'}`}>
              <div style={{ display: 'grid' }} className={`ov:px-0 ov:pb-63 ov:gap-2 ${getGridColsClass(gridDivide)}`}>
                <VariantsContent variantsToRender={variantsToRender} VariantCard={VariantCard} isMobile={isMobile} onSelect={onSelect} />
              </div>
            </div>
            {shouldShowFilters && (
              <FilterContent wrapperVariant="mobile" isOpen={isFilterOpen} />
            )}
          </div>
        </div>
      );
    }
  }
});

export const MobileVariants = React.memo(({variants, VariantCard, isMobile, onSelect, gridDivide, moduleTypeTabs} 
  : {
      variants: VariantGroup[] | Variant[], 
      VariantCard: React.ComponentType<VariantCardProps>,
      isMobile: boolean,
      onSelect: (variant: Variant) => void
      gridDivide: number,
      moduleTypeTabs?: React.ReactNode
  }) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    return (
      <MobileVariantsContent
        variants={variants}
        VariantCard={VariantCard}
        isMobile={isMobile}
        onSelect={onSelect}
        gridDivide={gridDivide}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        moduleTypeTabs={moduleTypeTabs}
      />
    );
}, (prevProps, nextProps) => {
  // Individual cards are already memoized, so we only need to check if the array reference changed
  // and if other props changed. The deep comparison is unnecessary since cards handle their own memoization.
  return (
    prevProps.variants === nextProps.variants &&
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.gridDivide === nextProps.gridDivide &&
    prevProps.VariantCard === nextProps.VariantCard &&
    prevProps.onSelect === nextProps.onSelect
  );
});